import axios, { AxiosError } from "axios"
import { Dispatch, RNBThunkAction } from "../store.config"
import { isAxiosError } from "../../utils/asyncTools"
import { ToastErrorProps } from "./merchantCodes.ducks"

export type SobankStatus = "idle" | "pending" | "connected" | "error"

export interface SobankConnection {
  id: number
  sobank_id: string
  status: SobankStatus
  status_message: string
}

export interface SobankStatusPayload {
  connections: SobankConnection[]
  connected_companies_ids: number[]
}

const enum SobankActionsEnum {
  CONNECT_SOBANK_USER_ATTEMPT = "SOBANK/connectSobankAttempt",
  CONNECT_SOBANK_USER_SUCCESS = "SOBANK/connectSobankSuccess",
  CONNECT_SOBANK_USER_ERROR = "SOBANK/connectSobankError",

  GET_SOBANK_STATUS_ATTEMPT = "SOBANK/getSobankStatusAttempt",
  GET_SOBANK_STATUS_SUCCESS = "SOBANK/getSobankStatusSuccess",
  GET_SOBANK_STATUS_ERROR = "SOBANK/getSobankStatusError",

  REQUEST_UPDATE_SOBANK_STATUS_ATTEMPT = "SOBANK/requestUpdateSobankStatusAttempt",
  REQUEST_UPDATE_SOBANK_STATUS_SUCCESS = "SOBANK/requestUpdateSobankStatusSuccess",
  REQUEST_UPDATE_SOBANK_STATUS_ERROR = "SOBANK/requestUpdateSobankStatusError",

  DISCONNECT_SOBANK_USER_ATTEMPT = "SOBANK/logoutSobankAttempt",
  DISCONNECT_SOBANK_USER_SUCCESS = "SOBANK/logoutSobankSuccess",
  DISCONNECT_SOBANK_USER_ERROR = "SOBANK/logoutSobankError",

  UPDATE_SOBANK_STATUS = "SOBANK/updateSobankStatus",
  UPDATE_CONNECTED_COMPANIES_IDS = "Sobank/updateConnectedCompaniesIds",
}

export const connectSobankAttemptAction = () =>
  ({ type: SobankActionsEnum.CONNECT_SOBANK_USER_ATTEMPT } as const)
export const connectSobankSuccessAction = () =>
  ({
    type: SobankActionsEnum.CONNECT_SOBANK_USER_SUCCESS,
    withToast: true,
    toasterType: "success",
    message: {
      titleKey: "fiduciary.sobank.connection.success.title",
      bodyKey: "fiduciary.sobank.connection.success.body",
    },
  } as const)
export const connectSobankErrorAction = (error: ToastErrorProps) =>
  ({
    type: SobankActionsEnum.CONNECT_SOBANK_USER_ERROR,
    withToast: true,
    toasterType: "error",
    message: {
      titleKey: error.titleKey,
      bodyKey: error.bodyKey,
      bodyValues: error.bodyValues,
    },
  } as const)

export const getSobankStatusAttemptAction = () =>
  ({ type: SobankActionsEnum.GET_SOBANK_STATUS_ATTEMPT } as const)
export const getSobankStatusSuccessAction = (payload: SobankStatusPayload) =>
  ({
    type: SobankActionsEnum.GET_SOBANK_STATUS_SUCCESS,
    payload,
  } as const)
export const getSobankStatusErrorAction = (error: ToastErrorProps) =>
  ({
    type: SobankActionsEnum.GET_SOBANK_STATUS_ERROR,
    withToast: true,
    toasterType: "error",
    message: {
      titleKey: error.titleKey,
      bodyKey: error.bodyKey,
      bodyValues: error.bodyValues,
    },
  } as const)

export const requestUpdateSobankStatusAttemptAction = () =>
  ({ type: SobankActionsEnum.REQUEST_UPDATE_SOBANK_STATUS_ATTEMPT } as const)
export const requestUpdateSobankStatusSuccessAction = () =>
  ({ type: SobankActionsEnum.REQUEST_UPDATE_SOBANK_STATUS_SUCCESS } as const)
export const requestUpdateSobankStatusErrorAction = (error: ToastErrorProps) =>
  ({
    type: SobankActionsEnum.REQUEST_UPDATE_SOBANK_STATUS_ERROR,
    withToast: true,
    toasterType: "error",
    message: {
      titleKey: error.titleKey,
      bodyKey: error.bodyKey,
      bodyValues: error.bodyValues,
    },
  } as const)

export const disconnectSobankAttemptAction = () =>
  ({ type: SobankActionsEnum.DISCONNECT_SOBANK_USER_ATTEMPT } as const)
export const disconnectSobankSuccessAction = () =>
  ({
    type: SobankActionsEnum.DISCONNECT_SOBANK_USER_SUCCESS,
    withToast: true,
    toasterType: "success",
    message: {
      titleKey: "fiduciary.sobank.logout.success.title",
      bodyKey: "fiduciary.sobank.logout.success.body",
    },
  } as const)
export const disconnectSobankErrorAction = (error: ToastErrorProps) =>
  ({
    type: SobankActionsEnum.DISCONNECT_SOBANK_USER_ERROR,
    withToast: true,
    toasterType: "error",
    message: {
      titleKey: error.titleKey,
      bodyKey: error.bodyKey,
      bodyValues: error.bodyValues,
    },
  } as const)

export const updateSobankStatusAction = (
  connection_id: number,
  connection_status: SobankStatus,
  connection_status_message: string
) =>
  ({
    type: SobankActionsEnum.UPDATE_SOBANK_STATUS,
    payload: { connection_id, connection_status, connection_status_message },
  } as const)

export const updateConnectedCompaniesIdsAction = (
  connected_companies_ids: number[]
) =>
  ({
    type: SobankActionsEnum.UPDATE_CONNECTED_COMPANIES_IDS,
    payload: { connected_companies_ids },
  } as const)

type SobankActionType = ReturnType<
  | typeof connectSobankAttemptAction
  | typeof connectSobankSuccessAction
  | typeof connectSobankErrorAction
  | typeof getSobankStatusAttemptAction
  | typeof getSobankStatusSuccessAction
  | typeof getSobankStatusErrorAction
  | typeof requestUpdateSobankStatusAttemptAction
  | typeof requestUpdateSobankStatusSuccessAction
  | typeof requestUpdateSobankStatusErrorAction
  | typeof disconnectSobankAttemptAction
  | typeof disconnectSobankSuccessAction
  | typeof disconnectSobankErrorAction
  | typeof updateSobankStatusAction
  | typeof updateConnectedCompaniesIdsAction
>

export interface SobankState {
  connections: SobankConnection[]
  connectedCompaniesIds: number[]
  getSobankStatus: "IDLE" | "LOADING" | "SUCCESS" | "ERROR"
  connectSobankStatus: "IDLE" | "LOADING" | "SUCCESS" | "ERROR"
  disconnectSobankStatus: "IDLE" | "LOADING" | "SUCCESS" | "ERROR"
  requestUpdateSobankStatus: "IDLE" | "LOADING" | "SUCCESS" | "ERROR"
}

export const sobankInitialState: SobankState = {
  connections: [],
  connectedCompaniesIds: [],
  getSobankStatus: "IDLE",
  connectSobankStatus: "IDLE",
  disconnectSobankStatus: "IDLE",
  requestUpdateSobankStatus: "IDLE",
}

export function sobankReducer(
  state = sobankInitialState,
  action: SobankActionType
): SobankState {
  switch (action.type) {
    case SobankActionsEnum.CONNECT_SOBANK_USER_ATTEMPT: {
      return { ...state, connectSobankStatus: "LOADING" }
    }
    case SobankActionsEnum.CONNECT_SOBANK_USER_SUCCESS: {
      return { ...state, connectSobankStatus: "SUCCESS" }
    }
    case SobankActionsEnum.CONNECT_SOBANK_USER_ERROR: {
      return { ...state, connectSobankStatus: "ERROR" }
    }
    case SobankActionsEnum.GET_SOBANK_STATUS_ATTEMPT: {
      return { ...state, getSobankStatus: "LOADING" }
    }
    case SobankActionsEnum.GET_SOBANK_STATUS_SUCCESS: {
      return {
        ...state,
        getSobankStatus: "SUCCESS",
        requestUpdateSobankStatus: "SUCCESS",
        connections: action.payload.connections,
        connectedCompaniesIds: action.payload.connected_companies_ids,
      }
    }
    case SobankActionsEnum.GET_SOBANK_STATUS_ERROR: {
      return { ...state, getSobankStatus: "ERROR" }
    }

    case SobankActionsEnum.REQUEST_UPDATE_SOBANK_STATUS_ATTEMPT: {
      return { ...state, requestUpdateSobankStatus: "LOADING" }
    }

    case SobankActionsEnum.REQUEST_UPDATE_SOBANK_STATUS_ERROR: {
      return { ...state, requestUpdateSobankStatus: "ERROR" }
    }

    case SobankActionsEnum.DISCONNECT_SOBANK_USER_ATTEMPT: {
      return { ...state, disconnectSobankStatus: "LOADING" }
    }
    case SobankActionsEnum.DISCONNECT_SOBANK_USER_SUCCESS: {
      return { ...state, disconnectSobankStatus: "SUCCESS" }
    }
    case SobankActionsEnum.DISCONNECT_SOBANK_USER_ERROR: {
      return { ...state, disconnectSobankStatus: "ERROR" }
    }

    case SobankActionsEnum.UPDATE_SOBANK_STATUS: {
      const { connection_id, connection_status, connection_status_message } =
        action.payload
      const newConnections = state.connections.map((connection) => {
        if (connection.id === connection_id) {
          return {
            ...connection,
            status: connection_status,
            status_message: connection_status_message,
          }
        }
        return connection
      })
      return { ...state, connections: newConnections }
    }

    case SobankActionsEnum.UPDATE_CONNECTED_COMPANIES_IDS: {
      return {
        ...state,
        connectedCompaniesIds: action.payload.connected_companies_ids,
        requestUpdateSobankStatus: "SUCCESS",
      }
    }

    default:
      return { ...state }
  }
}

const getSobankErrorMessages = (
  error: AxiosError | Error,
  defaultKey = "default"
) => {
  let key = defaultKey
  let bodyValues = {}

  if (
    isAxiosError(error) &&
    error.response !== undefined &&
    error.response.data
  ) {
    const errorMsg = JSON.stringify(error.response.data)
    if (errorMsg.includes("index_sobank_connections_on_sobank_id")) {
      key = "sobank-id-already-exists"
    }
  }

  return {
    titleKey: `fiduciary.sobank.error.${key}.title`,
    bodyKey: `fiduciary.sobank.error.${key}.body`,
    bodyValues,
  }
}

export const getStatusSobank =
  (fiduciaryId: number) =>
  (dispatch: Dispatch<SobankActionType | RNBThunkAction>) => {
    dispatch(getSobankStatusAttemptAction())
    return axios
      .get<SobankStatusPayload>(`fiduciaries/${fiduciaryId}/get_sobank_status`)
      .then((response) => {
        dispatch(getSobankStatusSuccessAction(response.data))
      })
      .catch((error: AxiosError) => {
        dispatch(getSobankStatusErrorAction(getSobankErrorMessages(error)))
      })
  }

export const connectSobank =
  (fiduciaryId: number, sobankIds: string[]) =>
  (dispatch: Dispatch<SobankActionType | RNBThunkAction>) => {
    dispatch(connectSobankAttemptAction())

    const data = { sobank_ids: sobankIds }

    return axios
      .post<SobankStatusPayload>(
        `fiduciaries/${fiduciaryId}/connect_sobank`,
        data
      )
      .then(() => {
        dispatch(connectSobankSuccessAction())
        dispatch(getStatusSobank(fiduciaryId))
      })
      .catch((error: AxiosError) => {
        dispatch(connectSobankErrorAction(getSobankErrorMessages(error)))
      })
  }

export const disconnectSobank =
  (fiduciaryId: number, connectionId: number) =>
  (dispatch: Dispatch<SobankActionType | RNBThunkAction>) => {
    dispatch(disconnectSobankAttemptAction())

    return axios
      .put<SobankStatusPayload>(
        `fiduciaries/${fiduciaryId}/disconnect_sobank`,
        { sobank_connection_id: connectionId }
      )
      .then(() => {
        dispatch(disconnectSobankSuccessAction())
        dispatch(getStatusSobank(fiduciaryId))
      })
      .catch((error: AxiosError) => {
        dispatch(disconnectSobankErrorAction(getSobankErrorMessages(error)))
      })
  }

export const requestUpdateSobankStatusThunk =
  (fiduciaryId: number) =>
  (dispatch: Dispatch<SobankActionType | RNBThunkAction>) => {
    dispatch(requestUpdateSobankStatusAttemptAction())

    return axios
      .put<SobankStatusPayload>(
        `fiduciaries/${fiduciaryId}/request_update_sobank_status`
      )
      .then(() => {
        dispatch(requestUpdateSobankStatusSuccessAction())
      })
      .catch((error: AxiosError) => {
        dispatch(
          requestUpdateSobankStatusErrorAction(getSobankErrorMessages(error))
        )
      })
  }

type ColorByStatusReturnType =
  | "amaranth"
  | "brightSun"
  | "shamrock"
  | "orange"
  | "columbia"
  | "cornflower"

export const SobankColorByStatus = (status: SobankStatus) => {
  const statuses: Record<SobankStatus, ColorByStatusReturnType> = {
    idle: "brightSun",
    pending: "orange",
    connected: "shamrock",
    error: "amaranth",
  }
  return statuses[status] || "brightSun"
}
